// Material exchange between a carrier (a bristle, a sponge pore, a knife blade)
// and one cell of the paint state. Every transfer moves material, never colour:
// what leaves the carrier arrives in the cell and vice versa, so mixing,
// contamination and depletion all follow from bookkeeping.
//
// A carrier holds:
//   water, pigW[k]  water and pigment suspended in it
//   body,  pigB[k]  paint body (vehicle) and pigment bound in it
//   capacity        how much water + body it can hold
//   medium          resolved medium parameters for paint it deposits

import { PIGMENT_SLOTS, markRect } from '../core/PaintState.js';
import { BODY_PRESENT } from './ViscousPaint.js';

export function createCarrier(capacity, medium) {
  return {
    water: 0,
    body: 0,
    pigW: new Float32Array(PIGMENT_SLOTS),
    pigB: new Float32Array(PIGMENT_SLOTS),
    capacity,
    medium,
  };
}

export function carrierFullness(carrier) {
  return Math.min(1, (carrier.water + carrier.body) / Math.max(1e-6, carrier.capacity));
}

// Load units: a carrier's fill is expressed in deposit-thickness units, so a
// full load of oil (body 1.0) lays a film about 1.0 thick, and a watercolour
// load (water ~0.75) lays a wash ~0.26 deep. WATER_LOAD and PIGMENT_LOAD map
// medium proportions to those units. Capacity leaves headroom for pickup.
export const WATER_LOAD = 0.9;
export const PIGMENT_LOAD = 1.5;
export const CAPACITY_HEADROOM = 1.6;

// Loads a carrier from the palette: a mixture of pigment slots (fractions summing to 1).
export function loadCarrier(carrier, medium, mixture, loading = 1) {
  const fill = (carrier.capacity / CAPACITY_HEADROOM) * Math.max(0, Math.min(1, loading));
  carrier.water = fill * medium.water * WATER_LOAD;
  carrier.body = fill * medium.body;
  const pigmentAmount = fill * medium.pigment * PIGMENT_LOAD;
  carrier.pigW.fill(0);
  carrier.pigB.fill(0);
  for (const { slot, fraction } of mixture) {
    const a = pigmentAmount * fraction;
    carrier.pigB[slot] += a * medium.binderGrip;
    carrier.pigW[slot] += a * (1 - medium.binderGrip);
  }
  carrier.medium = medium;
}

// Moves fraction f of one carrier's whole contents into another (bristle belly -> tip).
export function transferCarrier(from, to, f) {
  const frac = Math.max(0, Math.min(1, f));
  if (frac <= 0) return;
  const room = Math.max(0, to.capacity - to.water - to.body);
  const amount = (from.water + from.body) * frac;
  const scale = amount > room ? (room / Math.max(1e-12, amount)) * frac : frac;
  if (scale <= 0) return;
  const w = from.water * scale;
  const b = from.body * scale;
  from.water -= w;
  to.water += w;
  from.body -= b;
  to.body += b;
  for (let k = 0; k < PIGMENT_SLOTS; k += 1) {
    const aw = from.pigW[k] * scale;
    const ab = from.pigB[k] * scale;
    from.pigW[k] -= aw;
    to.pigW[k] += aw;
    from.pigB[k] -= ab;
    to.pigB[k] += ab;
  }
  if (!to.medium) to.medium = from.medium;
}

// Moves fraction f (0..1) of the carrier's contents into cell i.
export function depositToCell(state, i, carrier, f) {
  if (!(f > 0)) return;
  const frac = Math.min(1, f);
  const w = carrier.water * frac;
  if (w > 0) {
    state.water[i] += w;
    carrier.water -= w;
  }
  for (let k = 0; k < PIGMENT_SLOTS; k += 1) {
    const a = carrier.pigW[k] * frac;
    if (a > 0) {
      state.wetPig[k][i] += a;
      carrier.pigW[k] -= a;
    }
  }
  const b = carrier.body * frac;
  if (b <= 1e-9) {
    // No vehicle to hold it: binder-bound pigment behaves as suspended pigment.
    for (let k = 0; k < PIGMENT_SLOTS; k += 1) {
      const a = carrier.pigB[k] * frac;
      if (a > 0) {
        state.wetPig[k][i] += a;
        carrier.pigB[k] -= a;
      }
    }
  } else {
    const m = carrier.medium;
    const before = state.bodyVol[i];
    const after = before + b;
    const wNew = b / Math.max(1e-9, after);
    // Volume-weighted mixing of material properties.
    state.bodyMob[i] += (m.mobility - state.bodyMob[i]) * wNew;
    state.bodyYield[i] += (m.yieldStress - state.bodyYield[i]) * wNew;
    state.bodyDry[i] += (m.bodyDrying - state.bodyDry[i]) * wNew;
    state.bodySolids[i] += (m.heightGain - state.bodySolids[i]) * wNew;
    state.bodyGloss[i] += (m.wetGloss - state.bodyGloss[i]) * wNew;
    state.bodyOpen[i] += (1 - state.bodyOpen[i]) * wNew;
    state.bodyVol[i] = after;
    carrier.body -= b;
    for (let k = 0; k < PIGMENT_SLOTS; k += 1) {
      const a = carrier.pigB[k] * frac;
      if (a > 0) {
        state.bodyPig[k][i] += a;
        carrier.pigB[k] -= a;
      }
    }
  }
  if (w > 0) {
    // Re-wettability of whatever pigment later settles here.
    state.depRewet[i] += (carrier.medium.rewet - state.depRewet[i]) * Math.min(1, w / (state.water[i] + 1e-9));
  }
}

// Moves fraction f of the cell's mobile material (surface water with its
// pigment, and open wet body) into the carrier, limited by its free capacity.
export function pickupFromCell(state, i, carrier, f) {
  if (!(f > 0)) return;
  const room = Math.max(0, carrier.capacity - carrier.water - carrier.body);
  if (room <= 1e-9) return;
  const h = state.water[i];
  const v = state.bodyVol[i] > BODY_PRESENT ? state.bodyVol[i] * state.bodyOpen[i] : 0;
  const available = h + v;
  if (available <= 1e-9) return;
  const take = Math.min(room, available * Math.min(1, f));
  const fw = h > 0 ? (take * (h / available)) / h : 0;
  const fb = v > 0 ? (take * (v / available)) / state.bodyVol[i] : 0;
  if (fw > 0) {
    const w = h * fw;
    state.water[i] -= w;
    carrier.water += w;
    for (let k = 0; k < PIGMENT_SLOTS; k += 1) {
      const a = state.wetPig[k][i] * fw;
      if (a > 0) {
        state.wetPig[k][i] -= a;
        carrier.pigW[k] += a;
      }
    }
  }
  if (fb > 0) {
    const b = state.bodyVol[i] * fb;
    state.bodyVol[i] -= b;
    carrier.body += b;
    for (let k = 0; k < PIGMENT_SLOTS; k += 1) {
      const a = state.bodyPig[k][i] * fb;
      if (a > 0) {
        state.bodyPig[k][i] -= a;
        carrier.pigB[k] += a;
      }
    }
  }
}

// Moves fraction f of the open wet body at cell i to cell j (ploughing, knife pushes).
export function displaceBody(state, i, j, f) {
  if (!(f > 0) || state.bodyVol[i] <= BODY_PRESENT) return 0;
  const frac = Math.min(1, f * state.bodyOpen[i]);
  const moved = state.bodyVol[i] * frac;
  if (moved <= 0) return 0;
  const before = state.bodyVol[j];
  const wNew = moved / (before + moved);
  state.bodyMob[j] += (state.bodyMob[i] - state.bodyMob[j]) * wNew;
  state.bodyYield[j] += (state.bodyYield[i] - state.bodyYield[j]) * wNew;
  state.bodyDry[j] += (state.bodyDry[i] - state.bodyDry[j]) * wNew;
  state.bodySolids[j] += (state.bodySolids[i] - state.bodySolids[j]) * wNew;
  state.bodyGloss[j] += (state.bodyGloss[i] - state.bodyGloss[j]) * wNew;
  state.bodyOpen[j] += (state.bodyOpen[i] - state.bodyOpen[j]) * wNew;
  state.bodyVol[i] -= moved;
  state.bodyVol[j] += moved;
  for (let k = 0; k < PIGMENT_SLOTS; k += 1) {
    const a = state.bodyPig[k][i] * frac;
    if (a > 0) {
      state.bodyPig[k][i] -= a;
      state.bodyPig[k][j] += a;
    }
  }
  return moved;
}

export function touchCell(state, x, y) {
  markRect(state, x - 1, y - 1, x + 1, y + 1, true);
}
